import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sidebar, SidebarSection, Header, ThemeToggle } from '@trafiflow/shared-ui'
import { useAuth } from '@trafiflow/shared-ui'
import { ROUTES, APP_VERSION } from '../config'
import { MetaConnectionProvider } from '../contexts'
import { AlertsBell } from '../../modules/funnels'

interface LayoutProps {
  children: ReactNode
}

/**
 * Shared Layout Component
 *
 * Layout compartido usado por todos los módulos
 * Define la estructura general de la aplicación (navbar, footer, etc.)
 */
export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const sidebarSections: SidebarSection[] = [
    {
      items: [
        {
          id: 'home',
          title: 'Home',
          href: ROUTES.HOME,
          icon: 'Home',
        },
      ],
    },
    {
      title: 'Modules',
      items: [
        {
          id: 'stores',
          title: 'Stores',
          href: ROUTES.STORES.HOME,
          icon: 'ShoppingCart',
        },
        {
          id: 'agents',
          title: 'Agents',
          icon: 'Users',
          children: [
            {
              id: 'my_agents',
              title: 'My Agents',
              href: ROUTES.AGENTS.MY_AGENTS,
              icon: 'Bot',
            },
            {
              id: 'agent_market',
              title: 'Agent Market',
              href: ROUTES.AGENTS.MARKET,
              icon: 'ShoppingBag',
            },
            {
              id: 'agent_builder',
              title: 'Agent Builder',
              href: ROUTES.AGENTS.BUILDER,
              icon: 'Settings',
            },
          ],
        },
        {
          id: 'marketing',
          title: 'Marketing',
          href: ROUTES.MARKETING.HOME,
          icon: 'Megaphone',
        },
        {
          id: 'analytics',
          title: 'Analytics',
          icon: 'BarChart3',
          children: [
            {
              id: 'analytics-summary',
              title: 'Summary',
              href: ROUTES.ANALYTICS_SUMMARY,
              icon: 'TrendingUp',
            },
            {
              id: 'creatives',
              title: 'Creatives',
              href: ROUTES.CREATIVES,
              icon: 'Image',
            },
            {
              id: 'product-analysis',
              title: 'Product Analysis',
              href: ROUTES.CREATIVES_ANALYSIS,
              icon: 'FileBarChart',
            },
            {
              id: 'meta-config',
              title: 'Configuration',
              href: ROUTES.SETTINGS.META_CONFIG,
              icon: 'Settings',
            },
          ],
        },
        {
          id: 'my_company',
          title: 'My Company',
          href: ROUTES.MY_COMPANY.HOME,
          icon: 'Building',
        },
        {
          id: 'funnels',
          title: 'Funnels',
          icon: 'GitBranch',
          children: [
            {
              id: 'funnels_dashboard',
              title: 'Dashboard',
              href: ROUTES.FUNNELS.DASHBOARD,
              icon: 'LayoutDashboard',
            },
            {
              id: 'funnels_list',
              title: 'My Funnels',
              href: ROUTES.FUNNELS.HOME,
              icon: 'List',
            },
            {
              id: 'funnels_analytics',
              title: 'Analytics',
              href: ROUTES.FUNNELS.ANALYTICS,
              icon: 'BarChart3',
            },
            {
              id: 'funnels_offers',
              title: 'Offers',
              href: ROUTES.FUNNELS.OFFERS,
              icon: 'Tag',
            },
            {
              id: 'funnels_changelog',
              title: 'Changelog',
              href: ROUTES.FUNNELS.CHANGELOG,
              icon: 'FileText',
            },
            {
              id: 'funnels_alerts',
              title: 'Alerts',
              href: ROUTES.FUNNELS.ALERTS.HOME,
              icon: 'Bell',
            },
          ],
        },
      ],
    },
    {
      title: 'Admin',
      items: [
        {
          id: 'admin_organization',
          title: 'Organization',
          icon: 'Network',
          children: [
            {
              id: 'admin_orgchart',
              title: 'Org Chart',
              href: ROUTES.ADMIN.ORGANIZATION.ORGCHART,
              icon: 'GitBranch',
            },
            {
              id: 'admin_roles',
              title: 'Roles',
              href: ROUTES.ADMIN.ORGANIZATION.ROLES,
              icon: 'Shield',
            },
            {
              id: 'admin_people',
              title: 'People',
              href: ROUTES.ADMIN.ORGANIZATION.PEOPLE,
              icon: 'UserCheck',
            },
          ],
        },
        {
          id: 'billing',
          title: 'Billing',
          href: ROUTES.BILLING.HOME,
          icon: 'CreditCard',
        },
      ],
    },
  ]

  const handleLogout = async () => {
    try {
      await logout()
      window.location.href = ROUTES.LOGIN
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  return (
    <MetaConnectionProvider>
      <div className="flex min-h-screen bg-surface">
        <Sidebar sections={sidebarSections} version={APP_VERSION} />
        <div className="flex-1 flex flex-col min-w-0">
          <Header
            user={user ? {
              id: user.userId,
              email: user.email,
              name: user.email.split('@')[0],
              tenant_id: user.tenantId,
              role: user.roles?.[0],
            } : null}
            onLogout={handleLogout}
            onProfileClick={() => navigate(ROUTES.PROFILE)}
            searchPlaceholder="Search..."
            showSearch={true}
            notificationSlot={<AlertsBell />}
            themeSlot={<ThemeToggle />}
          />
          <main className="flex-1 p-6">
            {children}
          </main>
        </div>
      </div>
    </MetaConnectionProvider>
  )
}

